/**
 * StageSelectRenderer — grid for selecting a named stage.
 *
 * Appears after tank select, before round_start.
 * P1 navigates with WASD/Space, P2 with Arrows/Enter.
 * ESC goes back to tank select.
 */

import { Container, Graphics, Text, TextStyle } from 'pixi.js';
import { Keys } from 'bonkjs';
import { WORLD, MENU } from '../../constants';
import { getBackButtonLabel } from '../input-hints';
import { getAllStages, getAllStageIds, getStage } from '../../stages';
import { tickPreviewGeneration, getPreviewSprite, rerollRandomPreview } from './terrain-preview';
import type { GameState } from '../../types';

// ── Layout Constants ──────────────────────────────────────
const COLS = 4;
const ROWS = 3;
const CELL_W = 220;
const CELL_H = 155;
const CELL_GAP = 16;
const GRID_TOP_Y = 240;
const CELL_CORNER_RADIUS = 12;
const CELL_BG_ALPHA = 0.7;

// Preview area inside cell
const PREVIEW_X = 12;
const PREVIEW_Y = 12;
const PREVIEW_W = 196;
const PREVIEW_H = 98;

// Cursor bracket
const CURSOR_WIDTH = 3;
const CURSOR_BRACKET_SIZE = 20;
const CURSOR_PULSE_SPEED = 4;
const CURSOR_PULSE_MIN = 0.5;

function monoStyle(size: number, color: number, bold = false): TextStyle {
  return new TextStyle({
    fontFamily: '"JetBrains Mono", monospace',
    fontSize: size,
    fill: color,
    fontWeight: bold ? 'bold' : 'normal',
  });
}

export class StageSelectRenderer {
  private title: Text;
  private hint: Text;
  private cellContainers: Container[] = [];
  private cellBgs: Graphics[] = [];
  private cursorGfx: Graphics;
  private backButton: Text;
  private backHitRect: Graphics;
  // Preview placeholders (low-alpha rects shown before terrain preview loads)
  private previewPlaceholders: Graphics[] = [];
  // Preview sprites once generated
  private previewReady: boolean[] = [];
  // Track random preview seed for reroll detection
  private lastRandomSeed = 0;
  // Reroll hint text (only on random cell)
  private rerollHint: Text;
  // Random cell subtitle (shows theme name)
  private randomSubText: Text;
  // Multiplayer UI elements
  private mpPickLabel: Text;
  private mpWaitingText: Text;

  constructor(
    container: Container,
    tapVirtualKey: (code: string) => void,
    _getState: () => GameState | null,
  ) {
    const cx = WORLD.CANVAS_WIDTH / 2;
    const stages = getAllStages();

    // Full-screen event blocker
    const hitArea = new Graphics();
    hitArea.rect(0, 0, WORLD.CANVAS_WIDTH, WORLD.CANVAS_HEIGHT);
    hitArea.fill({ color: 0x000000, alpha: 0.001 });
    hitArea.eventMode = 'static';
    container.addChild(hitArea);

    // Title
    this.title = new Text({
      text: 'SELECT STAGE',
      style: monoStyle(MENU.MODE_SELECT_TITLE_SIZE, 0xffffff, true),
    });
    this.title.anchor.set(0.5);
    this.title.position.set(cx, 80);
    container.addChild(this.title);

    // Subtitle
    const subtitle = new Text({
      text: 'Choose your battlefield',
      style: monoStyle(16, 0x888888),
    });
    subtitle.anchor.set(0.5);
    subtitle.position.set(cx, 130);
    container.addChild(subtitle);

    // 4x2 Grid (7 items: 6 stages + Random)
    const stageIds = getAllStageIds();
    const totalCells = stageIds.length;
    const gridW = COLS * CELL_W + (COLS - 1) * CELL_GAP;
    const gridLeft = cx - gridW / 2;

    // We'll store reroll hint + random subtitle refs outside the loop
    this.rerollHint = new Text({ text: '', style: monoStyle(11, 0x888888) });
    this.rerollHint.visible = false;
    this.randomSubText = new Text({ text: '', style: monoStyle(11, 0x888888) });

    for (let i = 0; i < totalCells; i++) {
      const col = i % COLS;
      const row = Math.floor(i / COLS);
      const cellX = gridLeft + col * (CELL_W + CELL_GAP);
      const cellY = GRID_TOP_Y + row * (CELL_H + CELL_GAP);

      const cell = new Container();
      cell.position.set(cellX, cellY);
      container.addChild(cell);

      // Background rect
      const bg = new Graphics();
      bg.roundRect(0, 0, CELL_W, CELL_H, CELL_CORNER_RADIUS);
      bg.fill({ color: 0x0a0a19, alpha: CELL_BG_ALPHA });
      cell.addChild(bg);
      this.cellBgs.push(bg);

      // Preview placeholder (rounded rect with low-alpha theme color)
      const placeholder = new Graphics();
      if (stageIds[i] === 'random') {
        placeholder.roundRect(PREVIEW_X, PREVIEW_Y, PREVIEW_W, PREVIEW_H, 6);
        placeholder.fill({ color: 0x222233, alpha: 0.4 });
      } else if (i < stages.length) {
        const theme = stages[i]?.theme;
        if (theme) {
          const [sr, sg, sb] = theme.sandSurface;
          const terrainColor = (sr << 16) | (sg << 8) | sb;
          placeholder.roundRect(PREVIEW_X, PREVIEW_Y, PREVIEW_W, PREVIEW_H, 6);
          placeholder.fill({ color: terrainColor, alpha: 0.3 });
        }
      }
      cell.addChild(placeholder);
      this.previewPlaceholders.push(placeholder);
      this.previewReady.push(false);

      if (stageIds[i] === 'random') {
        // Random stage cell
        const nameText = new Text({
          text: 'RANDOM',
          style: monoStyle(16, 0xffffff, true),
        });
        nameText.anchor.set(0.5);
        nameText.position.set(CELL_W / 2, 118);
        cell.addChild(nameText);

        this.randomSubText = new Text({
          text: 'Procedural',
          style: monoStyle(11, 0x888888),
        });
        this.randomSubText.anchor.set(0.5);
        this.randomSubText.position.set(CELL_W / 2, 135);
        cell.addChild(this.randomSubText);

        this.rerollHint = new Text({
          text: 'R  REROLL',
          style: monoStyle(10, 0x666666),
        });
        this.rerollHint.anchor.set(0.5);
        this.rerollHint.position.set(CELL_W / 2, 148);
        cell.addChild(this.rerollHint);
      } else if (i < stages.length) {
        const stage = stages[i];
        const theme = stage?.theme;

        if (theme) {
          // Stage name
          const nameText = new Text({
            text: theme.name.toUpperCase(),
            style: monoStyle(16, theme.sandEdgeColor, true),
          });
          nameText.anchor.set(0.5);
          nameText.position.set(CELL_W / 2, 118);
          cell.addChild(nameText);

          // Subtitle
          const subText = new Text({
            text: theme.subtitle,
            style: monoStyle(11, 0x888888),
          });
          subText.anchor.set(0.5);
          subText.position.set(CELL_W / 2, 135);
          cell.addChild(subText);
        }
      }

      // Click hit rect
      const clickRect = new Graphics();
      clickRect.rect(0, 0, CELL_W, CELL_H);
      clickRect.fill({ color: 0x000000, alpha: 0.001 });
      clickRect.eventMode = 'static';
      clickRect.cursor = 'pointer';
      const cellIdx = i;
      clickRect.on('pointerdown', () => {
        const state = _getState();
        if (!state || i >= totalCells) return;
        // Non-chooser in multiplayer can't interact
        const mp = state.multiplayer;
        if (mp?.enabled && mp.mySlot !== mp.stageSelectChooser) return;
        state.stageSelectCursor = cellIdx;
        tapVirtualKey(Keys.Space);
      });
      clickRect.on('pointerover', () => {
        const state = _getState();
        if (!state || i >= totalCells) return;
        const mp = state.multiplayer;
        if (mp?.enabled && mp.mySlot !== mp.stageSelectChooser) return;
        state.stageSelectCursor = cellIdx;
      });
      cell.addChild(clickRect);

      this.cellContainers.push(cell);
    }

    // Cursor graphics
    this.cursorGfx = new Graphics();
    container.addChild(this.cursorGfx);

    // Back button
    this.backHitRect = new Graphics();
    this.backHitRect.eventMode = 'static';
    this.backHitRect.cursor = 'pointer';
    this.backHitRect.on('pointerdown', () => tapVirtualKey(Keys.Escape));
    container.addChild(this.backHitRect);

    this.backButton = new Text({
      text: getBackButtonLabel(),
      style: monoStyle(MENU.HINT_FONT_SIZE, MENU.HINT_COLOR),
    });
    this.backButton.anchor.set(0.5);
    this.backButton.position.set(cx - 300, WORLD.CANVAS_HEIGHT - 40);
    container.addChild(this.backButton);

    // Hint
    this.hint = new Text({
      text: '',
      style: monoStyle(MENU.HINT_FONT_SIZE, MENU.HINT_COLOR),
    });
    this.hint.visible = false;
    container.addChild(this.hint);

    // Multiplayer: pick label (shown below subtitle)
    this.mpPickLabel = new Text({
      text: '',
      style: monoStyle(20, 0x44ff44, true),
    });
    this.mpPickLabel.anchor.set(0.5);
    this.mpPickLabel.position.set(cx, 170);
    this.mpPickLabel.visible = false;
    container.addChild(this.mpPickLabel);

    // Multiplayer: waiting overlay text (for non-chooser)
    this.mpWaitingText = new Text({
      text: 'Opponent choosing stage...',
      style: monoStyle(28, 0x888888, true),
    });
    this.mpWaitingText.anchor.set(0.5);
    this.mpWaitingText.position.set(cx, WORLD.CANVAS_HEIGHT / 2);
    this.mpWaitingText.visible = false;
    container.addChild(this.mpWaitingText);
  }

  /** Reset preview flags so sprites are re-inserted on next stage select entry. */
  resetPreviews(): void {
    for (let i = 0; i < this.previewReady.length; i++) {
      this.previewReady[i] = false;
      this.previewPlaceholders[i].visible = true;
    }
    this.lastRandomSeed = 0;
  }

  update(state: GameState, elapsed: number): void {
    const cx = WORLD.CANVAS_WIDTH / 2;
    const gridW = COLS * CELL_W + (COLS - 1) * CELL_GAP;
    const gridLeft = cx - gridW / 2;
    const stageIds = getAllStageIds();

    // Generate one preview per frame
    tickPreviewGeneration();

    // Check for new preview sprites
    for (let i = 0; i < stageIds.length; i++) {
      if (this.previewReady[i]) continue;
      const sprite = getPreviewSprite(stageIds[i]);
      if (sprite) {
        // Swap placeholder for sprite
        sprite.position.set(PREVIEW_X, PREVIEW_Y);
        this.cellContainers[i].addChildAt(sprite, 2); // after bg + placeholder
        this.previewPlaceholders[i].visible = false;
        this.previewReady[i] = true;
      }
    }

    // Reroll detection — if seed changed, clear random preview flag
    const randomIdx = stageIds.indexOf('random');
    if (randomIdx >= 0 && state.randomPreviewSeed !== this.lastRandomSeed) {
      if (this.lastRandomSeed !== 0) {
        // Seed changed — reroll triggered. Remove old sprite if present.
        const oldSprite = getPreviewSprite('random');
        if (oldSprite && oldSprite.parent) {
          oldSprite.parent.removeChild(oldSprite);
        }
        this.previewReady[randomIdx] = false;
        this.previewPlaceholders[randomIdx].visible = true;
        rerollRandomPreview(state.randomPreviewSeed, state.randomPreviewThemeId);
        // Immediately check for the new sprite
        const newSprite = getPreviewSprite('random');
        if (newSprite) {
          newSprite.position.set(PREVIEW_X, PREVIEW_Y);
          this.cellContainers[randomIdx].addChildAt(newSprite, 2);
          this.previewPlaceholders[randomIdx].visible = false;
          this.previewReady[randomIdx] = true;
        }
      }
      this.lastRandomSeed = state.randomPreviewSeed;
    }

    // Update random cell subtitle with current theme name
    if (randomIdx >= 0) {
      const theme = getStage(state.randomPreviewThemeId).theme;
      this.randomSubText.text = theme.name;
    }

    // Clamp cursor to valid range
    const totalCells = this.cellContainers.length;
    if (state.stageSelectCursor >= totalCells) state.stageSelectCursor = totalCells - 1;

    // Draw cursor around selected cell
    this.cursorGfx.clear();
    const ci = state.stageSelectCursor;
    const col = ci % COLS;
    const row = Math.floor(ci / COLS);
    const cellX = gridLeft + col * (CELL_W + CELL_GAP);
    const cellY = GRID_TOP_Y + row * (CELL_H + CELL_GAP);

    const pulse = CURSOR_PULSE_MIN + (1 - CURSOR_PULSE_MIN) * (0.5 + 0.5 * Math.sin(elapsed * CURSOR_PULSE_SPEED));
    const pad = 6;
    const x0 = cellX - pad;
    const y0 = cellY - pad;
    const x1 = cellX + CELL_W + pad;
    const y1 = cellY + CELL_H + pad;
    const bk = CURSOR_BRACKET_SIZE;
    const color = 0xffffff;

    // Corner brackets
    this.cursorGfx.moveTo(x0, y0 + bk).lineTo(x0, y0).lineTo(x0 + bk, y0);
    this.cursorGfx.stroke({ color, width: CURSOR_WIDTH, alpha: pulse });
    this.cursorGfx.moveTo(x1 - bk, y0).lineTo(x1, y0).lineTo(x1, y0 + bk);
    this.cursorGfx.stroke({ color, width: CURSOR_WIDTH, alpha: pulse });
    this.cursorGfx.moveTo(x1, y1 - bk).lineTo(x1, y1).lineTo(x1 - bk, y1);
    this.cursorGfx.stroke({ color, width: CURSOR_WIDTH, alpha: pulse });
    this.cursorGfx.moveTo(x0 + bk, y1).lineTo(x0, y1).lineTo(x0, y1 - bk);
    this.cursorGfx.stroke({ color, width: CURSOR_WIDTH, alpha: pulse });

    // Highlight selected cell background
    for (let i = 0; i < this.cellBgs.length; i++) {
      const bg = this.cellBgs[i];
      bg.clear();
      bg.roundRect(0, 0, CELL_W, CELL_H, CELL_CORNER_RADIUS);
      if (i === ci) {
        bg.fill({ color: 0x1a1a3a, alpha: CELL_BG_ALPHA });
      } else {
        bg.fill({ color: 0x0a0a19, alpha: CELL_BG_ALPHA });
      }
    }

    // Show reroll hint only when cursor is on random cell
    this.rerollHint.visible = stageIds[ci] === 'random';

    // Multiplayer UI
    const mp = state.multiplayer;
    const isMP = !!mp?.enabled;
    const isChooser = isMP && mp!.mySlot === mp!.stageSelectChooser;
    const isNonChooser = isMP && !isChooser;

    if (isMP) {
      this.mpPickLabel.visible = true;
      if (isChooser) {
        this.mpPickLabel.text = 'YOUR PICK';
        this.mpPickLabel.style.fill = 0x44ff44;
      } else {
        const oppName = mp!.opponentName || 'OPPONENT';
        this.mpPickLabel.text = `${oppName.toUpperCase()}'S PICK`;
        this.mpPickLabel.style.fill = 0xff8844;
      }
    } else {
      this.mpPickLabel.visible = false;
    }

    // Non-chooser: show waiting overlay, hide cursor + cell interaction
    this.mpWaitingText.visible = isNonChooser;
    if (isNonChooser) {
      this.mpWaitingText.alpha = 0.5 + 0.5 * Math.sin(elapsed * 3);
    }
    this.cursorGfx.visible = !isNonChooser;

    // Hide reroll hint for non-chooser
    if (isNonChooser) {
      this.rerollHint.visible = false;
    }

    // Back button (hidden for non-chooser in MP)
    this.backButton.visible = !isNonChooser;
    this.backHitRect.visible = !isNonChooser;
  }
}
